/**
 * As funções das cordas e as cores que as identificam.
 *
 * Fica num arquivo de dados, longe da lógica: o instrutor muda uma cor ou
 * acrescenta uma função aqui sem mexer em mais nada. As cores seguem as
 * imagens de referência do plano; as marcadas com `corConfirmada: false` ainda
 * não aparecem nas fotos e esperam a confirmação do instrutor. Em tela, a cor
 * nunca vai sozinha: o painel sempre escreve o nome da função.
 */

export const IDS_FUNCAO_CORDA = ['livre', 'tracao', 'estabilizacao', 'tirolesa', 'fita', 'seguranca', 'vitima', 'talabarte'] as const

export type FuncaoCorda = (typeof IDS_FUNCAO_CORDA)[number]

export interface DefinicaoFuncaoCorda {
  readonly id: FuncaoCorda
  readonly rotulo: string
  readonly cor: string
  /** Para que serve, em uma frase, como aparece no painel. */
  readonly descricao: string
  /** `false` quando a cor ainda não foi confirmada pelo instrutor. */
  readonly corConfirmada: boolean
  /** Cordas desta função nascem com a seta de sentido de puxada. */
  readonly nascemComSeta: boolean
}

export const FUNCOES_CORDA: readonly DefinicaoFuncaoCorda[] = [
  {
    // a corda que se pega no catálogo e se prende nos pontos antes de decidir o que ela é
    id: 'livre',
    rotulo: 'Corda (sem função)',
    cor: '#9ca3af',
    descricao: 'Corda ainda sem função: prenda nos pontos e depois escolha o que ela é.',
    corConfirmada: true,
    nascemComSeta: false,
  },
  {
    id: 'tracao',
    rotulo: 'Tração / içamento',
    cor: '#facc15',
    descricao: 'Move a carga; é a corda que se puxa.',
    corConfirmada: true,
    nascemComSeta: true,
  },
  {
    id: 'estabilizacao',
    rotulo: 'Estabilização',
    cor: '#3b82f6',
    descricao: 'Segura a escada ou a estrutura no lugar.',
    corConfirmada: true,
    nascemComSeta: false,
  },
  {
    id: 'tirolesa',
    rotulo: 'Tirolesa guia',
    cor: '#22c55e',
    descricao: 'Trilho por onde a maca corre.',
    corConfirmada: true,
    nascemComSeta: false,
  },
  {
    id: 'fita',
    rotulo: 'Fita de ancoragem',
    cor: '#ef4444',
    descricao: 'Prende equipamento a um ponto de ancoragem.',
    corConfirmada: true,
    nascemComSeta: false,
  },
  {
    id: 'seguranca',
    rotulo: 'Segurança / backup',
    cor: '#a855f7',
    descricao: 'Segura a carga se o sistema principal falhar.',
    corConfirmada: false,
    nascemComSeta: false,
  },
  {
    id: 'vitima',
    rotulo: 'Linha da vítima',
    cor: '#ec4899',
    descricao: 'Ligação direta à maca ou à vítima.',
    corConfirmada: false,
    nascemComSeta: false,
  },
  {
    id: 'talabarte',
    rotulo: 'Talabarte do trabalhador',
    cor: '#f97316',
    descricao: 'Liga o engate dorsal do trabalhador a um ponto de ancoragem; é a corda da proteção contra queda.',
    corConfirmada: false,
    nascemComSeta: false,
  },
]

export const definicaoDaFuncao = (id: FuncaoCorda): DefinicaoFuncaoCorda => {
  const achada = FUNCOES_CORDA.find((funcao) => funcao.id === id)
  if (!achada) throw new Error(`Função de corda desconhecida: ${id}`)
  return achada
}

/** As funções que se escolhem para uma corda (todas menos a "sem função"). */
export const FUNCOES_ESCOLHIVEIS: readonly DefinicaoFuncaoCorda[] = FUNCOES_CORDA.filter((f) => f.id !== 'livre')
